import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { MewsBIIntegrationEnterprise, useRepositoryQuery } from '@mews-commander/data-access';
import { SelectField, SelectFieldProps } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { Icon, IconName, Tooltip } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { FormValues } from '../schema';

const TooltipIcon = Tooltip(Icon);

export const infoIconWithTooltip = (
    <TooltipIcon
        size="small"
        value={IconName.Info}
        tooltip={<LocalizedText textKey={TextKey.MewsBIIntegrationFormPropertiesTooltip} />}
    />
);

const VIRTUALIZED_OPTIONS_LIMIT = 50;

const renderOptions = (
    enterprises: MewsBIIntegrationEnterprise[],
    currentEnterpriseId: string,
    currentWorkspaceEnterpriseIds?: string[]
) =>
    enterprises
        .toSorted((a, b) => a.name.localeCompare(b.name))
        .map((property) =>
            createOption({
                value: property.id,
                label: property.name,
                disabled:
                    !currentWorkspaceEnterpriseIds?.includes(property.id) &&
                    (property.isAlreadyUsed || property.id === currentEnterpriseId),
            })
        );

interface PropertiesFieldProps extends Pick<SelectFieldProps, 'label' | 'name'> {
    currentWorkspaceEnterpriseIds?: string[];
}

export const PropertiesField = (props: PropertiesFieldProps) => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    const { data, isLoading } = useRepositoryQuery('mewsBI', 'getAllAvailableEnterprises', {});
    const enterprises = data?.enterprises || [];

    const [deselectUsed, setDeselectUsed] = useState(true);
    const { watch, setValue } = useFormContext<FormValues>();
    const selectedValues = watch('enterpriseIds');

    useEffect(() => {
        if (!selectedValues.includes(currentEnterpriseId)) {
            setValue('enterpriseIds', [...selectedValues, currentEnterpriseId]);
        }
    }, [selectedValues, currentEnterpriseId, setValue]);

    return (
        <SelectField
            {...props}
            compact
            captionIconWithTooltip={infoIconWithTooltip}
            loader={isLoading}
            multiselect
            onSelectAll={() => setDeselectUsed(!deselectUsed)}
            options={renderOptions(enterprises, currentEnterpriseId, props.currentWorkspaceEnterpriseIds)}
            virtualized={enterprises.length > VIRTUALIZED_OPTIONS_LIMIT}
        />
    );
};
